/** Layer styling configuration for spatial visualization. */

export interface LayerStyle {
  type: 'fill' | 'line';
  color: string;
  opacity: number;
  width?: number; // line layers only
  minZoom: number;
  maxZoom: number;
  selectable: boolean;
  /** Rendering order: lower = rendered first (bottom), higher = on top */
  renderOrder: number;
  /** Selection highlight color override (default: #fbbf24 gold) */
  selectedColor?: string;
  /** Outline color when unselected */
  outlineColor?: string;
}

/**
 * Layer styles ordered from largest/background to smallest/foreground.
 * renderOrder controls stacking: low = bottom, high = top.
 * minZoom/maxZoom control scale-based visibility.
 *
 * Reference implementation zoom tiers:
 *   Overview (0-15):  inverter_base, ug_conduit_34.5kv only
 *   Detail (16-18):   + ug_conduit, combiner_*, inverter_boxed
 *   Full (19+):       + rack_outlines
 *   Hidden:           site_polygon, site_layout_line (noise layers)
 */
export const LAYER_STYLES: Record<string, LayerStyle> = {
  // --- Hidden layers (background noise) ---
  site_polygon:              { type: 'fill', color: '#BDC3C7', opacity: 0.1,  minZoom: 0,  maxZoom: 14, selectable: false, renderOrder: 0 },
  site_layout_line:          { type: 'line', color: '#95A5A6', opacity: 0.3,  width: 1, minZoom: 0,  maxZoom: 14, selectable: false, renderOrder: 1 },

  // --- Infrastructure lines (bottom) ---
  ug_conduit:                { type: 'line', color: '#9B59B6', opacity: 0.6,  width: 2, minZoom: 14, maxZoom: 25, selectable: false, renderOrder: 10 },
  'ug_conduit_34.5kv':       { type: 'line', color: '#E74C3C', opacity: 0.7,  width: 3, minZoom: 0,  maxZoom: 25, selectable: false, renderOrder: 11 },
  'ug_conduit_34.5kv_1ckt':  { type: 'line', color: '#C0392B', opacity: 0.7,  width: 3, minZoom: 0,  maxZoom: 25, selectable: false, renderOrder: 12 },

  // --- Large areas (mid) ---
  inverter_boxed:            { type: 'fill', color: '#FF9F1C', opacity: 0.2,  minZoom: 15, maxZoom: 25, selectable: false, renderOrder: 20 },
  rack_outlines:             { type: 'fill', color: '#8B5CF6', opacity: 0.3,  minZoom: 17, maxZoom: 25, selectable: false, renderOrder: 21 },

  // --- Selectable component areas (top) ---
  combiner_boxes:            { type: 'fill', color: '#17C480', opacity: 0.4,  minZoom: 15, maxZoom: 25, selectable: true, renderOrder: 30, outlineColor: '#10B981' },
  combiner_outlines:         { type: 'fill', color: '#2EC4B6', opacity: 0.5,  minZoom: 15, maxZoom: 25, selectable: true, renderOrder: 31, outlineColor: '#2EC4B6' },
  inverter_base:             { type: 'fill', color: '#FF6B35', opacity: 0.3,  minZoom: 0,  maxZoom: 25, selectable: true, renderOrder: 40, outlineColor: '#87CEEB' },
};

/** Selection highlight color — gold/amber for all layers */
export const SELECTION_COLOR = '#fbbf24';

/** Default style for unknown layer types. */
export const DEFAULT_LAYER_STYLE: LayerStyle = {
  type: 'fill',
  color: '#888888',
  opacity: 0.3,
  minZoom: 0,
  maxZoom: 25,
  selectable: false,
  renderOrder: 15,
};

/** Get style for a layer type, falling back to default. */
export function getLayerStyle(layerType: string): LayerStyle {
  return LAYER_STYLES[layerType] || DEFAULT_LAYER_STYLE;
}

/**
 * Returns layer types sorted by renderOrder (bottom to top).
 * This ensures correct visual stacking in the map.
 */
export function getSortedLayerTypes(layerTypes: string[]): string[] {
  return [...layerTypes].sort((a, b) => {
    const sa = LAYER_STYLES[a]?.renderOrder ?? 15;
    const sb = LAYER_STYLES[b]?.renderOrder ?? 15;
    return sa - sb;
  });
}
